import { createElement, lazy, Suspense } from 'react';
import { ComposeAiDraftPlaceholder } from '../features/aiDrafts/ComposeAiDraftPlaceholder';
import type { ComposeAiDraftPlaceProps, ComposeEmailComposition } from '../lib/email/composeEmail';
import { reportError } from './telemetry';

/**
 * What fills the compose dialog's `aiDraft` place (F20-c FR-C8; WI-1.2 D-16). The composition lives
 * here, in the composition root, so the email feature never imports the feature that fills it.
 *
 * F11-h fills it with its *AI draft* button (F11-h spec FR-V4), from its own folder, as a `lazy()`
 * import so the button's code stays in its own chunk. Neither the email nor the leads feature imports
 * `src/features/aiDrafts` (F11-h spec FR-V6).
 *
 * **While the chunk loads, the place holds the button's room** with a hidden, inert stand-in
 * (`ComposeAiDraftPlaceholder`, F11-h WI-1.2 plan `## Notes`, fix-2). With nothing there, the button arrived
 * after the dialog had settled and moved *Insert template* left in the toolbar's right-aligned end group; a
 * press that the arrival split ended outside *Insert template* and opened nothing.
 *
 * A filler's render error stays inside the place and goes to the telemetry seam, never to the console.
 */

/** The *AI draft* button's chunk (F11-h FR-V4) — exported for its test. */
export const loadComposeAiDraftButton = () =>
    import('../features/aiDrafts/ComposeAiDraftButton').then((module) => ({
        default: module.ComposeAiDraftButton,
    }));

const LazyComposeAiDraftButton = lazy(loadComposeAiDraftButton);

/**
 * The button once its chunk has loaded, its stand-in until then. Written without JSX because this file keeps
 * its `.ts` name, which `e2e/fixtures/routes.setup.ts` reads to load every lazy compose module.
 */
function ComposeAiDraftPlace(props: ComposeAiDraftPlaceProps) {
    return createElement(
        Suspense,
        { fallback: createElement(ComposeAiDraftPlaceholder, props) },
        createElement(LazyComposeAiDraftButton, props),
    );
}

export const composeEmailComposition: ComposeEmailComposition = {
    aiDraft: ComposeAiDraftPlace,
    reportPlaceError: (error) => reportError({ error, where: 'compose aiDraft place' }),
};
